import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { Table } from "antd";

/* ================= COMPONENTS ================= */
const DashboardTile = ({
    title,
    icon,
    count,
    link,
    colorClass = "text-primary",
}: {
    title: string;
    icon?: string;
    count?: string | number;
    link: string;
    colorClass?: string;
}) => {
    return (
        <Link to={link || "#"} className="text-decoration-none col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12 mb-3">
            <div
                className="card h-100 border-0 shadow-sm hover-card"
                style={{
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                }}
            >
                <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-4">

                    <div
                        className={`d-flex align-items-center justify-content-center mb-2 rounded-circle`}
                        style={{
                            width: "56px",
                            height: "56px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
                            border: "1px solid rgba(0,0,0,0.03)"
                        }}
                    >
                        {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "28px" }}></i>}
                    </div>

                    <h6 className="fw-bold text-dark mb-1 w-100 px-1 text-truncate" title={title} style={{ fontSize: "14px", letterSpacing: "0.2px" }}>
                        {title}
                    </h6>

                    {count !== undefined && (
                        <div className="mt-1">
                            <span className={`fs-4 fw-bold ${colorClass}`}>
                                {count}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <div className="mb-4">
            <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {title}
            </h5>
            <div className="row">{children}</div>
        </div>
    );
};

/* ================= MAIN ================= */
const COOverview: React.FC = () => {
    const routes = all_routes;

    // Mock data for CO
    const [requests, setRequests] = useState([
        {
            key: "1",
            empNo: "201045",
            name: "Rajesh Kumar",
            quarterType: "Type-II",
            location: "Yamuna Bank",
            requestType: "Fresh Allotment",
            date: "2026-02-01",
            status: "Pending",
        },
        {
            key: "2",
            empNo: "202112",
            name: "Amit Singh",
            quarterType: "Type-III",
            location: "Shastri Park",
            requestType: "Change of Flat",
            date: "2026-02-03",
            status: "Pending",
        },
        {
            key: "3",
            empNo: "203445",
            name: "Sunita Verma",
            quarterType: "Type-II",
            location: "Mundka",
            requestType: "Fresh Allotment",
            date: "2026-02-05",
            status: "Pending",
        },
    ]);

    const stats = {
        pending: requests.length,
        approved: 45,
        rejected: 12,
        total: 57 + requests.length
    };

    const handleAction = (key: string, action: 'Approve' | 'Reject') => {
        // Logic to handle approval/rejection
        alert(`${action} request for ID: ${key}`);
        setRequests(prev => prev.filter(r => r.key !== key));
    };

    const columns = [
        {
            title: "Employee",
            dataIndex: "name",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm me-2 bg-primary-light text-primary rounded-circle d-flex align-items-center justify-content-center fw-bold">
                        {text.charAt(0)}
                    </div>
                    <div>
                        <div className="fw-bold text-dark">{text}</div>
                        <div className="text-muted small">{record.empNo}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Quarter Type",
            dataIndex: "quarterType",
            render: (text: string) => <span className="badge bg-light text-dark border">{text}</span>
        },
        {
            title: "Location",
            dataIndex: "location",
        },
        {
            title: "Request Type",
            dataIndex: "requestType",
        },
        {
            title: "Submission Date",
            dataIndex: "date",
            render: (date: string) => {
                const d = new Date(date);
                const day = String(d.getDate()).padStart(2, "0");
                const month = d.toLocaleString('default', { month: 'short' });
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");

                return (
                    <div className="d-flex flex-column">
                        <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
                            <i className="ti ti-calendar-event me-1 text-muted"></i>
                            {`${day} ${month}, ${year}`}
                        </span>
                        <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
                            <i className="ti ti-clock me-1"></i>
                            {`${hours}:${minutes}`}
                        </span>
                    </div>
                );
            }
        },
        {
            title: "Actions",
            key: "actions",
            render: (record: any) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-success d-flex align-items-center"
                        onClick={() => handleAction(record.key, 'Approve')}
                    >
                        <i className="ti ti-check me-1"></i> Approve
                    </button>
                    <button
                        className="btn btn-sm btn-danger d-flex align-items-center"
                        onClick={() => handleAction(record.key, 'Reject')}
                    >
                        <i className="ti ti-x me-1"></i> Reject
                    </button>
                    <Link to={routes.staffQuarterAdmin.requests} className="btn btn-sm btn-light border">
                        <i className="ti ti-eye"></i>
                    </Link>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">CO Approval Dashboard</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Staff Quarters CO</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Stats Section */}
                <DashboardSection title="Approval Overview">
                    <DashboardTile
                        title="Pending Approvals"
                        icon="ti-clock"
                        count={stats.pending}
                        link="#"
                        colorClass="text-warning"
                    />
                    <DashboardTile
                        title="Total Approved"
                        icon="ti-circle-check"
                        count={stats.approved}
                        link="#"
                        colorClass="text-success"
                    />
                    <DashboardTile
                        title="Total Rejected"
                        icon="ti-circle-x"
                        count={stats.rejected}
                        link="#"
                        colorClass="text-danger"
                    />
                    <DashboardTile
                        title="Total Handled"
                        icon="ti-list-details"
                        count={stats.total}
                        link="#"
                        colorClass="text-info"
                    />
                </DashboardSection>

                {/* Active Queue */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '16px 16px 0 0' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0 fw-bold">My Approval Queue</h5>
                            <span className="badge bg-primary-light text-primary px-3 py-2 rounded-pill">
                                {requests.length} Requests Awaiting
                            </span>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <Table
                                columns={columns}
                                dataSource={requests}
                                pagination={false}
                                className="table-hover"
                            />
                        </div>
                    </div>
                </div>

                <style>{`
                    .hover-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
                    }
                    .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.1); }
                    .avatar-sm { width: 32px; height: 32px; font-size: 14px; }
                `}</style>
            </div>
        </div>
    );
};

export default COOverview;
